import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  Clock,
  TrendingUp,
  DollarSign,
  User,
  Building2,
  Bell,
  History,
  Plus,
  MessageCircle,
  Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { CallLogModal } from "../components/common/CallLogModal";
import { CustomerTagBadge } from "../components/common/CustomerTagBadge";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate, formatDateTime, cn, getTelLink, getWhatsAppLink } from "../lib/utils";
import { toast } from "sonner";

export const CustomerProfilePage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [branches, setBranches] = useState([]);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [callLogContact, setCallLogContact] = useState(null);
  
  // New service form
  const [newService, setNewService] = useState({
    service_date: new Date(),
    service_type: "",
    amount: "",
    notes: "",
    branch_id: ""
  });

  useEffect(() => {
    fetchCustomerProfile();
    fetchBranches();
  }, [customerId]);

  const fetchCustomerProfile = async () => {
    try {
      const data = await apiRequest(`/customers/${customerId}/profile`);
      setProfile(data);
    } catch (error) {
      toast.error("Failed to fetch customer profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await apiRequest("/services", {
        method: "POST",
        body: JSON.stringify({
          customer_id: customerId,
          service_date: newService.service_date.toISOString(),
          service_type: newService.service_type,
          amount: parseFloat(newService.amount),
          branch_id: newService.branch_id || profile.customer.branch_id,
          notes: newService.notes,
          created_by: user.id
        })
      });
      toast.success("Service recorded successfully");
      setShowAddServiceModal(false);
      setNewService({
        service_date: new Date(),
        service_type: "",
        amount: "",
        notes: "",
        branch_id: ""
      });
      fetchCustomerProfile();
    } catch (error) {
      toast.error(error.message || "Failed to record service");
    }
  };

  const handleCall = () => {
    setCallLogContact({
      id: customerId,
      name: profile.customer.name,
      phone: profile.customer.phone,
      status: profile.customer.status,
      type: "customer",
      branch_id: profile.customer.branch_id,
      tag: profile.customer.tag || "normal"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Customer not found</p>
        <Button onClick={() => navigate("/customers")} className="mt-4">
          Back to Customers
        </Button>
      </div>
    );
  }

  const { customer, service_history, service_intelligence, reminder_status, call_history, assigned_cre, branch } = profile;

  return (
    <div className="space-y-6" data-testid="customer-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/customers")} data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold">{customer.name}</h1>
            <StatusBadge status={customer.status} />
            <CustomerTagBadge 
              customerId={customerId} 
              currentTag={customer.tag || "normal"} 
              onUpdate={fetchCustomerProfile}
            />
          </div>
          <p className="text-muted-foreground font-mono">{formatPhone(customer.phone)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.location.href = getTelLink(customer.phone)} data-testid="call-customer-btn">
            <Phone className="w-4 h-4 mr-2" />
            Call
          </Button>
          <Button variant="outline" onClick={() => window.open(getWhatsAppLink(customer.phone), "_blank")} data-testid="whatsapp-customer-btn">
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Dialog open={showAddServiceModal} onOpenChange={setShowAddServiceModal}>
            <DialogTrigger asChild>
              <Button data-testid="add-service-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle className="font-heading">Record New Service</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddService} className="space-y-4">
                <div>
                  <Label>Service Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal")}
                        data-testid="service-date-trigger"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {format(newService.service_date, "dd MMM yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker
                        mode="single"
                        selected={newService.service_date}
                        onSelect={(date) => setNewService({ ...newService, service_date: date || new Date() })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="service_type">Service Type *</Label>
                  <Input
                    id="service_type"
                    value={newService.service_type}
                    onChange={(e) => setNewService({ ...newService, service_type: e.target.value })}
                    placeholder="e.g., Hair Treatment, Full Service"
                    required
                    data-testid="service-type-input"
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Amount (₹) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    value={newService.amount}
                    onChange={(e) => setNewService({ ...newService, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="service-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="branch">Branch</Label>
                  <Select
                    value={newService.branch_id}
                    onValueChange={(value) => setNewService({ ...newService, branch_id: value })}
                  >
                    <SelectTrigger data-testid="service-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={newService.notes}
                    onChange={(e) => setNewService({ ...newService, notes: e.target.value })}
                    rows={2}
                    data-testid="service-notes-input"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowAddServiceModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-service-btn">
                    Save Service
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Customer Info & Intelligence Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Basic Info */}
        <Card className="bento-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Customer Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {customer.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{customer.email}</span>
              </div>
            )}
            {branch && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span>{branch.name}</span>
              </div>
            )}
            {assigned_cre && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>CRE: {assigned_cre.name}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>Since: {formatDate(customer.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Service Interval */}
        <Card className="bento-card" data-testid="avg-interval-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Service Interval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {service_intelligence.average_interval_days || "-"}
                </p>
                <p className="text-xs text-muted-foreground">days (last 3 services)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Spending */}
        <Card className="bento-card" data-testid="spending-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Service Spending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  ₹{service_intelligence.last_3_services_total?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-muted-foreground">
                  Avg: ₹{service_intelligence.average_service_spend?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Service Prediction */}
        <Card className="bento-card" data-testid="prediction-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Next Service</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading">
                  {service_intelligence.next_service_prediction 
                    ? formatDate(service_intelligence.next_service_prediction)
                    : "Not available"}
                </p>
                <p className="text-xs text-muted-foreground">predicted based on history</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reminder Status */}
      {reminder_status && (
        <Card className="bento-card border-yellow-200 bg-yellow-50/50" data-testid="reminder-status-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-yellow-600" />
              <CardTitle className="text-sm font-medium">Reminder Status</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">SMS (75 days)</p>
                <p className={reminder_status.sms_sent ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {reminder_status.sms_sent ? "Sent" : formatDate(reminder_status.reminder_75_days)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">WhatsApp (85 days)</p>
                <p className={reminder_status.whatsapp_sent ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {reminder_status.whatsapp_sent ? "Sent" : formatDate(reminder_status.reminder_85_days)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Call Task (90 days)</p>
                <p className={reminder_status.call_task_created ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {reminder_status.call_task_created ? "Created" : formatDate(reminder_status.reminder_90_days)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for History */}
      <Tabs defaultValue="services" className="space-y-4">
        <TabsList>
          <TabsTrigger value="services" data-testid="services-tab">
            Service History ({service_history.length})
          </TabsTrigger>
          <TabsTrigger value="calls" data-testid="calls-tab">
            Call History ({call_history.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services">
          <Card className="bento-card">
            <CardContent className="p-0">
              <div className="data-table-container">
                <Table>
                  <TableHeader>
                    <TableRow className="table-high-density">
                      <TableHead>Date</TableHead>
                      <TableHead>Service Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {service_history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No service history found
                        </TableCell>
                      </TableRow>
                    ) : (
                      service_history.map((service, index) => (
                        <TableRow key={service.id || index} className="table-high-density" data-testid={`service-row-${index}`}>
                          <TableCell className="font-mono text-sm">
                            {formatDate(service.service_date)}
                          </TableCell>
                          <TableCell className="font-medium">{service.service_type}</TableCell>
                          <TableCell className="text-right font-mono">
                            ₹{service.amount?.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {branches.find(b => b.id === service.branch_id)?.name || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                            {service.notes || "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calls">
          <Card className="bento-card">
            <CardContent className="p-0">
              <div className="data-table-container">
                <Table>
                  <TableHeader>
                    <TableRow className="table-high-density">
                      <TableHead>Date/Time</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Remark</TableHead>
                      <TableHead>Follow-up</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {call_history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No call history found
                        </TableCell>
                      </TableRow>
                    ) : (
                      call_history.map((call, index) => (
                        <TableRow key={call.id || index} className="table-high-density" data-testid={`call-row-${index}`}>
                          <TableCell className="font-mono text-sm">
                            {formatDateTime(call.created_at)}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              call.outcome === "answered" ? "bg-green-100 text-green-800" :
                              call.outcome === "no_answer" ? "bg-yellow-100 text-yellow-800" :
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {call.outcome?.replace("_", " ").toUpperCase()}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm max-w-[300px] truncate">
                            {call.remark || "-"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {call.follow_up_date ? formatDate(call.follow_up_date) : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Call Log Modal */}
      <CallLogModal
        open={!!callLogContact}
        onClose={() => setCallLogContact(null)}
        contact={callLogContact}
        onSuccess={fetchCustomerProfile}
      />
    </div>
  );
};

export default CustomerProfilePage;
